import React from 'react';
import InovxLoader from '@shared/global/InovxLoader';

interface DragState {
    startY: number;
    startPosition: number;
}

type PaneContent = React.ReactNode | ((height: number) => React.ReactNode);

interface CustomHorizontalSplitterProps {
    containerHeight: number;
    minTopHeight: number;
    minBottomHeight: number;
    initialPosition: number;
    topPanel: PaneContent;
    bottomPanel?: PaneContent;
    showBottomPanel?: boolean;
    className?: string;
    handleAriaLabel?: string;
    followInitialPositionUntilResized?: boolean;
}

const SPLITTER_HANDLE_HEIGHT = 10;
const SPLITTER_TOP_GAP = 8;

const renderPane = (content: PaneContent | undefined, height: number) => {
    if (typeof content === 'function') {
        return content(height);
    }

    return content;
};

const CustomHorizontalSplitter: React.FC<CustomHorizontalSplitterProps> = ({
    containerHeight,
    minTopHeight,
    minBottomHeight,
    initialPosition,
    topPanel,
    bottomPanel,
    showBottomPanel = true,
    className = '',
    handleAriaLabel,
    followInitialPositionUntilResized = false,
}) => {
    const resolvedHandleAriaLabel = handleAriaLabel ?? InovxLoader.t('common:panel.resizePanels');
    const dragStateRef = React.useRef<DragState | null>(null);
    const previousShowBottomPanelRef = React.useRef(showBottomPanel);
    const hasUserResizedRef = React.useRef(false);
    const [topHeight, setTopHeight] = React.useState(initialPosition);

    const clampTopHeight = React.useCallback((value: number) => {
        if (!showBottomPanel) {
            return containerHeight;
        }

        const maxTopHeight = Math.max(minTopHeight, containerHeight - minBottomHeight - SPLITTER_HANDLE_HEIGHT);
        return Math.min(Math.max(value, minTopHeight), maxTopHeight);
    }, [containerHeight, minBottomHeight, minTopHeight, showBottomPanel]);

    React.useEffect(() => {
        const visibilityChanged = previousShowBottomPanelRef.current !== showBottomPanel;
        previousShowBottomPanelRef.current = showBottomPanel;
        if (visibilityChanged) {
            hasUserResizedRef.current = false;
        }

        const shouldUseInitialPosition =
            visibilityChanged ||
            (followInitialPositionUntilResized && !hasUserResizedRef.current);
        setTopHeight(previous =>
            clampTopHeight(shouldUseInitialPosition ? initialPosition : previous));
    }, [
        clampTopHeight,
        followInitialPositionUntilResized,
        initialPosition,
        showBottomPanel,
    ]);

    const handleResizeMove = React.useCallback((event: MouseEvent) => {
        const dragState = dragStateRef.current;
        if (!dragState) {
            return;
        }

        const delta = event.clientY - dragState.startY;
        setTopHeight(clampTopHeight(dragState.startPosition + delta));
    }, [clampTopHeight]);

    const stopResize = React.useCallback(() => {
        dragStateRef.current = null;
        document.body.classList.remove('custom-horizontal-splitter-resizing');
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', stopResize);
    }, [handleResizeMove]);

    const handleResizeStart = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (!showBottomPanel) {
            return;
        }

        event.preventDefault();
        hasUserResizedRef.current = true;
        dragStateRef.current = {
            startY: event.clientY,
            startPosition: topHeight,
        };

        document.body.classList.add('custom-horizontal-splitter-resizing');
        document.addEventListener('mousemove', handleResizeMove);
        document.addEventListener('mouseup', stopResize);
    }, [handleResizeMove, showBottomPanel, stopResize, topHeight]);

    React.useEffect(() => {
        return () => {
            document.body.classList.remove('custom-horizontal-splitter-resizing');
            document.removeEventListener('mousemove', handleResizeMove);
            document.removeEventListener('mouseup', stopResize);
        };
    }, [handleResizeMove, stopResize]);

    const effectiveTopHeight = showBottomPanel ? clampTopHeight(topHeight) : containerHeight;
    const topGap = showBottomPanel ? SPLITTER_TOP_GAP : 0;
    const topContentHeight = Math.max(0, effectiveTopHeight - topGap);
    const bottomHeight = showBottomPanel
        ? Math.max(containerHeight - effectiveTopHeight - SPLITTER_HANDLE_HEIGHT, minBottomHeight)
        : 0;
    const splitterClassName = className ? `custom-horizontal-splitter ${className}` : 'custom-horizontal-splitter';

    return (
        <div className={splitterClassName} style={{ height: containerHeight }}>
            <div className="custom-horizontal-splitter-pane custom-horizontal-splitter-pane-top" style={{ height: effectiveTopHeight, paddingBottom: topGap, boxSizing: 'border-box', flexShrink: 0 }}>
                {renderPane(topPanel, topContentHeight)}
            </div>

            {showBottomPanel && (
                <>
                    <div
                        className="custom-horizontal-splitter-handle"
                        role="separator"
                        aria-orientation="horizontal"
                        aria-label={resolvedHandleAriaLabel}
                        onMouseDown={handleResizeStart}
                    >
                        <div className="separator">
                           <i className="fa fa-ellipsis-h" aria-hidden="true"></i>
                        </div>
                    </div>
                    <div className="custom-horizontal-splitter-pane custom-horizontal-splitter-pane-bottom" style={{ height: bottomHeight }}>
                        {renderPane(bottomPanel, bottomHeight)}
                    </div>
                </>
            )}
        </div>
    );
};

export default CustomHorizontalSplitter;
